import { useQuery } from '@apollo/client';
import { EFilterType, TFilter, TFilterEvent } from 'Components/Filter/filter.types';
import { useLogEvent } from 'hooks/useLogEvent';
import { DOCUMENTS_PER_PAGE } from 'utils/pagination.utils';
import { GET_DOCUMENTS, GET_SHARED_DOCUMENTS } from 'graphql/secure/queries';
import moment from 'moment';
import { useMemo } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { EDocumentCategory, IBaseDocument } from 'FeatureModules/Document/types/document.types';
import { EDocumentLibraryTabs } from 'FeatureModules/Document/types/document.types';
import { IGraphQlConnection } from 'types/core.types';
import {
  createEnumArrayParam,
  ObjectParam,
  StringParam,
  useQueryParams,
  withDefault,
} from 'use-query-params';
import { removeGraphConnections } from 'utils/graphql.utils';

const DATE_FORMAT = 'YYYY-MM-DD';

const usePlotDocuments = () => {
  const { plotId, id: siteId } = useParams();
  const { logEvent } = useLogEvent();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const [query, setQuery] = useQueryParams(
    {
      searchTerm: withDefault(StringParam, ''),
      creationDateRange: ObjectParam,
      categories: withDefault(createEnumArrayParam(Object.keys(EDocumentCategory)), null),
    },
    { removeDefaultsFromUrl: true }
  );

  const { searchTerm, creationDateRange, categories } = query;

  const selectedTab: EDocumentLibraryTabs = pathname.includes('/shared')
    ? EDocumentLibraryTabs.SHARED
    : EDocumentLibraryTabs.INTERNAL;

  const {
    data: documentsData,
    loading: documentsLoading,
    error: documentsError,
    fetchMore: fetchMoreDocuments,
  } = useQuery<{
    documents: IGraphQlConnection<IBaseDocument>;
  }>(GET_DOCUMENTS, {
    variables: {
      filters: {
        searchTerm,
        plotIds: plotId ? [plotId] : undefined,
        creationDateRange,
        categories,
      },
      first: DOCUMENTS_PER_PAGE,
    },
    skip: !plotId || selectedTab !== EDocumentLibraryTabs.INTERNAL,
    fetchPolicy: 'network-only',
  });

  const {
    data: sharedDocumentsData,
    loading: sharedDocumentsLoading,
    error: sharedDocumentsError,
    fetchMore: fetchMoreSharedDocuments,
  } = useQuery<{
    sharedDocuments: IGraphQlConnection<IBaseDocument>;
  }>(GET_SHARED_DOCUMENTS, {
    variables: {
      filters: {
        searchTerm,
        plotIds: plotId ? [plotId] : undefined,
        creationDateRange,
        categories,
      },
      first: DOCUMENTS_PER_PAGE,
    },
    skip: !plotId || selectedTab !== EDocumentLibraryTabs.SHARED,
    fetchPolicy: 'network-only',
  });

  const documents = documentsData?.documents ? removeGraphConnections(documentsData.documents) : [];
  const sharedDocuments = sharedDocumentsData?.sharedDocuments
    ? removeGraphConnections(sharedDocumentsData.sharedDocuments)
    : [];

  const hasNextPage: boolean =
    selectedTab === EDocumentLibraryTabs.INTERNAL
      ? !!documentsData?.documents?.pageInfo?.hasNextPage
      : !!sharedDocumentsData?.sharedDocuments?.pageInfo?.hasNextPage;
  const endCursor =
    selectedTab === EDocumentLibraryTabs.INTERNAL
      ? documentsData?.documents?.pageInfo?.endCursor
      : sharedDocumentsData?.sharedDocuments?.pageInfo?.endCursor;

  const filters: TFilter[] = useMemo(
    () => [
      {
        type: EFilterType.DOCUMENT_CATEGORY,
        value: categories,
      },
      {
        type: EFilterType.TIME_FRAME,
        label: 'Upload date',
        value:
          creationDateRange?.startDate || creationDateRange?.endDate
            ? {
                startDate: creationDateRange?.startDate
                  ? new Date(creationDateRange.startDate)
                  : undefined,
                endDate: creationDateRange?.endDate
                  ? new Date(creationDateRange.endDate)
                  : undefined,
              }
            : null,
      },
    ],
    [categories, creationDateRange]
  );

  const handlePageEndReached = () => {
    const fetchMore =
      selectedTab === EDocumentLibraryTabs.INTERNAL ? fetchMoreDocuments : fetchMoreSharedDocuments;
    if (endCursor) {
      fetchMore({
        variables: {
          after: endCursor,
        },
      });
    }
  };

  const handleSearchTermChange = (searchTerm: string) => {
    setQuery({ searchTerm });
    logEvent('PLOT_DOCUMENTS_SEARCHED', { searchTerm });
  };

  const handleFilterChange = (event: TFilterEvent) => {
    if (event.type === EFilterType.TIME_FRAME) {
      const { startDate, endDate } = event.value || {};

      setQuery({
        creationDateRange: {
          startDate: startDate ? moment(startDate).format(DATE_FORMAT) : undefined,
          endDate: endDate ? moment(endDate).format(DATE_FORMAT) : undefined,
        },
      });
    } else if (event.type === EFilterType.DOCUMENT_CATEGORY) {
      setQuery({
        ...event,
        categories: event.value,
      });
    }
    logEvent('PLOT_DOCUMENTS_FILTERED', { type: event.type });
  };

  const handleTabChange = (tab: EDocumentLibraryTabs) => {
    if (tab === EDocumentLibraryTabs.INTERNAL) {
      navigate(`/sites/${siteId}/plots/${plotId}/documents/internal`);
    } else {
      navigate(`/sites/${siteId}/plots/${plotId}/documents/${tab}`);
    }
  };

  const handleFilterReset = () => {
    setQuery({
      searchTerm: '',
      creationDateRange: undefined,
      categories: undefined,
    });
  };

  return {
    searchTerm,
    filters,
    documents: selectedTab === EDocumentLibraryTabs.INTERNAL ? documents : sharedDocuments,
    loading: documentsLoading || sharedDocumentsLoading,
    error: documentsError || sharedDocumentsError,
    hasNextPage,
    endCursor,
    selectedTab,
    handleTabChange,
    handlePageEndReached,
    handleSearchTermChange,
    handleFilterChange,
    handleFilterReset,
  };
};

export default usePlotDocuments;
